import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, EffectFade } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { 
  Button, 
  Section, 
  Card,
  FadeInUp,
  SlideIn,
  StaggerContainer,
  StaggerItem,
} from '../../components/common';
import { DESTINATIONS, HOME_HERO_SLIDES, HOME_DESTINATIONS_GRID } from '../../constants';
import { usePageTitle } from '../../hooks/usePageTitle';

export default function Home() {
  usePageTitle('Home');
  const countriesCount = 40;
  const [activeIndex, setActiveIndex] = useState(0);
  const currentPhoto = HOME_HERO_SLIDES[activeIndex];

  const handleSlideChange = (swiper: SwiperType) => {
    setActiveIndex(swiper.realIndex);
  };

  return (
    <>
      {/* Hero Section with Photo Slider */}
      <section className="relative min-h-screen">
        <Swiper
          modules={[Autoplay, Pagination, EffectFade]}
          effect="fade"
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          loop={true}
          onSlideChange={handleSlideChange}
          className="absolute inset-0 w-full h-full"
        >
          {HOME_HERO_SLIDES.map((photo, index) => (
            <SwiperSlide key={photo.id} className="relative">
              <div 
                className="absolute inset-0 w-full h-full bg-cover bg-center"
                style={{ backgroundImage: `url(${photo.url})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-earth-900/80 via-earth-900/40 to-earth-900/30" />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Hero Content */}
        <div className="relative z-10 min-h-screen flex items-center pointer-events-none">
          <div className="container-custom pointer-events-auto pt-20 sm:pt-24 pb-12 sm:pb-16">
            <div className="max-w-4xl">
              <FadeInUp>
                <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6 border border-white/20">
                  <i className="ri-global-line"></i>
                  Inglês através de experiências reais
                </span>
              </FadeInUp>
              
              <FadeInUp delay={0.1}>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 sm:mb-6 leading-tight">
                   Beyond Class é sobre vivenciar o idioma, abrir portas,{' '}
                  <span className="text-sand-300">realizar sonhos e transformar vidas.</span>
                </h1>
              </FadeInUp>
              
              <FadeInUp delay={0.2}>
                <p className="text-base sm:text-lg md:text-xl text-sand-200 mb-8 max-w-4xl leading-relaxed">
                  Na Beyond Class, o inglês é vivido — não apenas ensinado.
                  Você aprende com professores que construíram sua fluência morando, estudando e trabalhando em diferentes países, transformando vivência internacional em aulas significativas e transformadoras.
                </p>
              </FadeInUp>

              <FadeInUp delay={0.3}>
                <div className="grid grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-8 sm:mb-10">
                  <div className="text-center">
                    <p className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-sand-300">40+</p>
                    <p className="text-sand-200 text-[10px] sm:text-xs md:text-sm">países visitados</p>
                  </div>
                  <div className="text-center border-x border-sand-400/30 px-1 sm:px-2">
                    <p className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-sand-300">500+</p>
                    <p className="text-sand-200 text-[10px] sm:text-xs md:text-sm">alunos formados</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-sand-300">15</p>
                    <p className="text-sand-200 text-[10px] sm:text-xs md:text-sm">anos de experiência</p>
                  </div>
                </div>
              </FadeInUp>

              <FadeInUp delay={0.4}>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button to="/contato" size="lg" icon="ri-arrow-right-line">
                    Agende seu teste de nivelamento grátis
                  </Button>
                  <Button 
                    to="/sobre" 
                    variant="outline" 
                    size="lg"
                    className="border-sand-300 text-sand-100 hover:bg-sand-100 hover:text-earth-900"
                  >
                    Conheça nossa história
                  </Button>
                </div>
              </FadeInUp>
            </div>
          </div>
        </div>
      </section>

      {/* Countries Section - De onde vem nossas histórias */}
      <Section bgColor="sand">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <SlideIn direction="left">
            <p className="text-olive-600 font-medium mb-2 flex items-center gap-2 text-sm sm:text-base">
              <i className="ri-compass-3-line"></i>
              Nossas aventuras
            </p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-earth-800 mb-4 sm:mb-6">
              De onde vêm nossa história
            </h2>
            <p className="text-earth-600 mb-4 leading-relaxed text-sm sm:text-base">
              O inglês sempre foi mais do que uma língua pra mim — foi a chave que abriu portas, transformou sonhos e me conectou com o mundo. 
            </p>
            <p className="text-earth-600 mb-4 leading-relaxed text-sm sm:text-base">
              Não é apenas a minha profissão, é o meu propósito: ensinar de um jeito que vá além da sala de aula, transformando o inglês em experiência e caminho para realizações. 
            </p>
            <p className="text-earth-600 mb-5 sm:mb-6 leading-relaxed text-sm sm:text-base">
              Hoje, através do Beyond Class, quero compartilhar essa visão: aprender inglês é viver novas histórias, dentro e fora da sala de aula.
            </p>
            <div className="flex flex-wrap gap-2">
              {DESTINATIONS.map((dest) => (
                <span
                  key={dest.country}
                  className="inline-flex items-center gap-1 sm:gap-1.5 bg-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[11px] sm:text-xs text-earth-700 shadow-sm border border-sand-200"
                >
                  <i className="ri-map-pin-2-line text-olive-600 text-[10px] sm:text-xs"></i>
                  {dest.city}, {dest.country}
                </span>
              ))}
            </div>
          </SlideIn>
          
          <SlideIn direction="right">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img 
                  src={HOME_DESTINATIONS_GRID[0].url} 
                  alt={HOME_DESTINATIONS_GRID[0].alt} 
                  className="rounded-xl shadow-md w-full h-48 object-cover"
                />
                <img 
                  src={HOME_DESTINATIONS_GRID[1].url} 
                  alt={HOME_DESTINATIONS_GRID[1].alt} 
                  className="rounded-xl shadow-md w-full h-32 object-cover"
                />
              </div>
              <div className="space-y-4 pt-8">
                <img 
                  src={HOME_DESTINATIONS_GRID[2].url} 
                  alt={HOME_DESTINATIONS_GRID[2].alt} 
                  className="rounded-xl shadow-md w-full h-32 object-cover"
                />
                <img 
                  src={HOME_DESTINATIONS_GRID[3].url} 
                  alt={HOME_DESTINATIONS_GRID[3].alt} 
                  className="rounded-xl shadow-md w-full h-48 object-cover"
                />
              </div>
            </div>
          </SlideIn>
        </div>
      </Section>

      {/* Why Choose Us Section */}
      <Section bgColor="white">
        <FadeInUp className="text-center mb-8 sm:mb-12">
          <p className="text-olive-600 font-medium mb-2 text-sm sm:text-base">Por que nos escolher?</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-earth-800 mb-3 sm:mb-4">
            Uma abordagem diferente para aprender inglês
          </h2>
          <p className="text-earth-600 max-w-2xl mx-auto text-sm sm:text-base">
            Combinamos metodologia moderna com experiências reais para criar um aprendizado 
            que faz sentido para o seu dia a dia.
          </p>
        </FadeInUp>

        <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 md:gap-8">
          {[
            {
              icon: 'ri-global-line',
              title: 'Vivência Internacional',
              description: 'Nossos professores moraram no exterior e trazem o inglês real do dia a dia, com expressões, gírias e nuances culturais que você só aprende vivendo.',
            },
            {
              icon: 'ri-lightbulb-line',
              title: 'Metodologia Imersiva',
              description: 'Nossas aulas simulam situações reais que você vai encontrar em viagens, trabalho e conversas. Nada de decorar listas — aqui você pratica de verdade.',
            },
            {
              icon: 'ri-route-line',
              title: 'Caminho Personalizado',
              description: 'Cada aluno tem objetivos diferentes. Montamos um plano de estudos adaptado às suas metas, seja para viajar, trabalhar ou fazer intercâmbio.',
            },
          ].map((feature) => (
            <StaggerItem key={feature.title}>
              <Card className="text-center h-full">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-olive-100 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <i className={`${feature.icon} text-xl sm:text-2xl text-olive-600`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-earth-800 mb-2 sm:mb-3">
                  {feature.title}
                </h3>
                <p className="text-earth-600 leading-relaxed text-sm sm:text-base">
                  {feature.description}
                </p>
              </Card>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </Section>

      {/* Services Preview */}
      <Section bgColor="olive">
        <FadeInUp className="text-center mb-8 sm:mb-12">
          <p className="text-olive-700 font-medium mb-2 text-sm sm:text-base">Nossos serviços</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-earth-800 mb-3 sm:mb-4">
            Escolha o formato ideal para você
          </h2>
          <p className="text-earth-600 max-w-2xl mx-auto text-sm sm:text-base">
            Oferecemos diferentes modalidades de ensino para atender às suas necessidades e estilo de vida.
          </p>
        </FadeInUp>

        <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
          {[
            { 
              title: 'Aulas Particulares', 
              desc: 'Atenção total para você, com aulas personalizadas focadas nos seus objetivos específicos.', 
              path: '/servicos/aulas-particulares', 
              icon: 'ri-user-line',
            },
            { 
              title: 'Imersão ao Ar Livre', 
              desc: 'Aprenda inglês em cafés, parques e lugares reais. Pratique como se estivesse no exterior.', 
              path: '/servicos/ar-livre', 
              icon: 'ri-plant-line',
            },
            { 
              title: 'Preparação para Intercâmbio', 
              desc: 'Prepare-se para sua experiência internacional com quem já viveu isso na prática.', 
              path: '/servicos/intercambio', 
              icon: 'ri-plane-line',
            },
            { 
              title: 'Aulas Online', 
              desc: 'Flexibilidade para estudar de onde estiver, mantendo a qualidade das aulas presenciais.', 
              path: '/servicos/cursos-online', 
              icon: 'ri-computer-line',
            },
          ].map((service) => (
            <StaggerItem key={service.path}>
              <Card className="h-full flex flex-col">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-olive-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                  <i className={`${service.icon} text-lg sm:text-xl text-olive-600`}></i>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-earth-800 mb-2">
                  {service.title}
                </h3>
                <p className="text-earth-600 text-sm mb-3 sm:mb-4 flex-grow">{service.desc}</p>
                <Button to={service.path} variant="ghost" size="sm" icon="ri-arrow-right-s-line">
                  Saiba mais
                </Button>
              </Card>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </Section>

      {/* Testimonial / Quote Section */}
      <Section bgColor="white">
        <FadeInUp className="max-w-3xl mx-auto text-center">
          <i className="ri-double-quotes-l text-4xl sm:text-5xl text-sand-300 mb-3 sm:mb-4"></i>
          <blockquote className="text-xl sm:text-2xl md:text-3xl text-earth-700 font-light italic mb-5 sm:mb-6 leading-relaxed">
            "A melhor forma de aprender um idioma é vivendo ele. Com a Beyond Class, 
            você aprende com quem realmente viveu essa experiência."
          </blockquote>
          <div className="flex items-center justify-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-olive-200 rounded-full flex items-center justify-center">
              <i className="ri-user-line text-olive-600"></i>
            </div>
            <div className="text-left">
              <p className="font-semibold text-earth-800 text-sm sm:text-base">Equipe Beyond Class</p>
              <p className="text-earth-600 text-xs sm:text-sm">Professores e viajantes</p>
            </div>
          </div>
        </FadeInUp>
      </Section>

      {/* CTA Section */}
      <Section bgColor="dark">
        <FadeInUp className="text-center max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-sand-100 mb-3 sm:mb-4">
            Pronto para começar sua jornada?
          </h2>
          <p className="text-sand-300 text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed">
            Agende seu teste de nivelamento gratuito e descubra uma nova forma de aprender inglês. 
            Sem compromisso, sem pressão — apenas uma conversa para conhecer você e seus objetivos.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button 
              to="/contato" 
              size="lg" 
              className="bg-olive-200 text-olive-900 hover:bg-white"
              icon="ri-calendar-line"
            >
              Agendar aula grátis
            </Button>
          </div>
        </FadeInUp>
      </Section>
    </>
  );
}
